import React, { useState, useEffect, useRef , useMemo } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Save, Upload, Building2, FileText, Landmark, Image as ImageIcon, Loader2 } from 'lucide-react';
import { getAuthToken } from '../lib/authToken';
import { CreneauxSettings } from '../components/settings/CreneauxSettings';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingTampon, setUploadingTampon] = useState(false);
  const logoRef = useRef(null);
  const tamponRef = useRef(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/settings`, { headers });
        setSettings(res.data);
      } catch (err) {
        toast.error('Erreur chargement des parametres');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  // eslint-disable-next-line
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings`, settings, { headers });
      toast.success('Parametres enregistres');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (type) => {
    const ref = type === 'logo' ? logoRef : tamponRef;
    const file = ref.current?.files?.[0];
    if (!file) return;
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingTampon;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/admin/upload-${type}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${type === 'logo' ? 'Logo' : 'Tampon'} mis a jour`);
      setSettings(prev => ({ ...prev, [`has_${type}`]: true }));
    } catch {
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const F = (field) => (e) => setSettings({ ...settings, [field]: e.target.value });
  const FNum = (field) => (e) => setSettings({ ...settings, [field]: parseFloat(e.target.value) || 0 });

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Administration</h1>
            <p className="text-sm text-slate-500">Configuration de l'organisme et des documents</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>

        {/* Identite */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Identite de l'organisme
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={settings.nom || ''} onChange={F('nom')} data-testid="setting-nom" />
              </div>
              <div className="space-y-2">
                <Label>Denomination</Label>
                <Input value={settings.denomination || ''} onChange={F('denomination')} placeholder="Ex: SARL au capital de 10000 EUR" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Representant legal</Label>
                <Input value={settings.representant || ''} onChange={F('representant')} data-testid="setting-representant" />
              </div>
              <div className="space-y-2">
                <Label>Capital social</Label>
                <Input value={settings.capital || ''} onChange={F('capital')} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input value={settings.adresse || ''} onChange={F('adresse')} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input value={settings.code_postal || ''} onChange={F('code_postal')} />
              </div>
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input value={settings.ville || ''} onChange={F('ville')} />
              </div>
              <div className="space-y-2">
                <Label>Siege social</Label>
                <Input value={settings.siege_social || ''} onChange={F('siege_social')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={settings.email || ''} onChange={F('email')} />
              </div>
              <div className="space-y-2">
                <Label>Telephone</Label>
                <Input value={settings.telephone || ''} onChange={F('telephone')} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email de facturation électronique</Label>
              <Input type="email" value={settings.email_facturation || ''} onChange={F('email_facturation')} placeholder="Adresse de réception / émission des factures électroniques (AGROPE)" data-testid="settings-email-facturation" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Activite</Label>
                <Input value={settings.activite || ''} onChange={F('activite')} />
              </div>
              <div className="space-y-2">
                <Label>Site web</Label>
                <Input value={settings.site_web || ''} onChange={F('site_web')} placeholder="https://..." />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Numeros legaux */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5 text-emerald-600" />
              Numeros legaux
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input value={settings.siret || ''} onChange={F('siret')} data-testid="setting-siret" />
              </div>
              <div className="space-y-2">
                <Label>RCS</Label>
                <Input value={settings.rcs || ''} onChange={F('rcs')} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Code APE/NAF</Label>
                <Input value={settings.ape || ''} onChange={F('ape')} />
              </div>
              <div className="space-y-2">
                <Label>TVA Intracommunautaire</Label>
                <Input value={settings.tva_intra || ''} onChange={F('tva_intra')} />
              </div>
              <div className="space-y-2">
                <Label>N° Declaration d'Activite (DNA)</Label>
                <Input value={settings.dna || ''} onChange={F('dna')} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parametres financiers */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Landmark className="w-5 h-5 text-amber-600" />
              Parametres financiers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Taux de TVA en vigueur (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={settings.taux_tva ?? 20} onChange={FNum('taux_tva')} data-testid="setting-tva" />
                <p className="text-[11px] text-slate-500">Appliqué automatiquement à toutes les prochaines factures et avoirs. Les factures déjà émises conservent leur taux.</p>
              </div>
              <div className="space-y-2">
                <Label>Conditions de paiement</Label>
                <Input value={settings.conditions_paiement || ''} onChange={F('conditions_paiement')} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Banque</Label>
                <Input value={settings.banque_nom || ''} onChange={F('banque_nom')} />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input value={settings.banque_iban || ''} onChange={F('banque_iban')} />
              </div>
              <div className="space-y-2">
                <Label>BIC</Label>
                <Input value={settings.banque_bic || ''} onChange={F('banque_bic')} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Penalites de retard</Label>
              <Textarea value={settings.penalites_retard || ''} onChange={F('penalites_retard')} rows={2} />
            </div>
          </CardContent>
        </Card>

        {/* Images */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <ImageIcon className="w-5 h-5 text-purple-600" />
              Logo et tampon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-3">
                <Label>Logo (affiche en haut a droite des documents)</Label>
                <div className="flex items-center gap-3 flex-wrap">
                  <input type="file" ref={logoRef} accept="image/*" className="hidden" onChange={() => handleUpload('logo')} />
                  <Button type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn">
                    {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    {settings.has_logo ? 'Remplacer le logo' : 'Charger un logo'}
                  </Button>
                  {settings.has_logo && <span className="text-xs text-green-600 font-medium">Logo actif</span>}
                </div>
              </div>
              <div className="space-y-3">
                <Label>Tampon de signature (affiche dans le cadre signature)</Label>
                <div className="flex items-center gap-3 flex-wrap">
                  <input type="file" ref={tamponRef} accept="image/*" className="hidden" onChange={() => handleUpload('tampon')} />
                  <Button type="button" variant="outline" size="sm" onClick={() => tamponRef.current?.click()} disabled={uploadingTampon} data-testid="upload-tampon-btn">
                    {uploadingTampon ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    {settings.has_tampon ? 'Remplacer le tampon' : 'Charger un tampon'}
                  </Button>
                  {settings.has_tampon && <span className="text-xs text-green-600 font-medium">Tampon actif</span>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <CreneauxSettings creneaux={settings.creneaux} onChange={(cr) => setSettings({ ...settings, creneaux: cr })} />
      </div>
    </DashboardLayout>
  );
};
